import React, { useEffect, useState, useRef } from 'react';
import {
  Button,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PropTypes from 'prop-types';
import ApexCharts from 'src/components/apexcharts/ApexCharts';
import axiosInstance from 'src/utils/axiosInstance';

const WidgetRadarChart = ({
  containerWidth,
  careersReskillTime,
  onDataLoaded,
  showLabel,
  closeLabel,
  openLabel,
  compact,
  chartHeight: chartHeightProp,
  tone,
}) => {
  const [radarData, setRadarData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [noData, setNoData] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // compact chart: no labels on the chart, the skill nearest to the pointer is named in a small chip
  const [hover, setHover] = useState(null);
  const wrapRef = useRef(null);
  const thm = useTheme();
  const isXs = useMediaQuery(thm.breakpoints.down('sm'));
  const isMobile = useMediaQuery(thm.breakpoints.down(1025));
  // the callback is a new function on every render of the page: keep the latest one without making the effect depend on it
  const onDataLoadedRef = useRef(onDataLoaded);
  onDataLoadedRef.current = onDataLoaded;

  // the chart is asked for once per list of careers (and again when "Try again" is pressed)
  const careerIds = [];
  (careersReskillTime?.sectorOrSubsector || []).forEach((sector) => {
    (sector.careers || []).forEach((career) => {
      if (career.id) careerIds.push(career.id);
    });
  });
  const careerIdsKey = careerIds.join(',');

  useEffect(() => {
    if (!careerIdsKey) {
      setIsLoading(false);
      setNoData(true);
      return undefined;
    }
    let ignore = false;

    const fetchRadarData = async () => {
      try {
        setIsLoading(true);
        setFailed(false);
        setNoData(false);

        const response = await axiosInstance({
          url: 'frontend/getSkilldarAverage',
          method: 'POST',
          data: { careerIds },
        });
        if (ignore) return;

        // none of the careers has skills data: say so instead of drawing an empty chart
        if (response.data.data.noData) {
          setNoData(true);
          return;
        }

        const chartData = {
          series: response.data.data.series,
          options: {
            fill: {
              opacity: 0.6,
              colors: [],
            },
            tooltip: {
              enabled: !compact,
              shared: false,
              intersect: true,
              style: {
                fontSize: '12px',
              },
            },
            xaxis: {
              categories: response.data.data.options.xaxis.categories.map((label) =>
                label?.toUpperCase()
              ),
              tickPlacement: 'on',
              labels: {
                show: !compact,
                style: {
                  fontSize: compact ? '10px' : '12px',
                  fontWeight: 700,
                  colors: Array(10).fill('#000000'),
                },
              },
            },
            grid: {
              borderColor: '#3067DE',
            },
            plotOptions: {
              radar: {
                polygons: {
                  strokeColors: ['#FD4645', '#FFB55B', '#2AA24A', '#933DA9', '#3067DE'],
                },
              },
            },
            legend: {
              show: false,
            },
            yaxis: {
              max: 100,
              tickAmount: 5,
              min: 0,
              show: false,
            },
            chart: {
              animations: {
                enabled: true,
              },
              toolbar: {
                show: false,
              },
            },
            states: {
              hover: {
                filter: {
                  type: 'lighten',
                  value: 0.15,
                },
              },
            },
            markers: {
              size: compact ? 3 : 4,
              colors: undefined,
              strokeColors: '#fff',
              strokeWidth: 2,
              strokeOpacity: 0.9,
              strokeDashArray: 0,
              fillOpacity: 1,
              shape: 'circle',
              hover: {
                size: 7,
                sizeOffset: 3,
              },
            },
          },
        };
        setRadarData(chartData);

        if (onDataLoadedRef.current) {
          onDataLoadedRef.current(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching radar data:', error);
        if (!ignore) setFailed(true);
      } finally {
        if (!ignore) setIsLoading(false);
      }
    };

    fetchRadarData();
    return () => {
      ignore = true;
    };
  }, [careerIdsKey, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const chartWidth = containerWidth > 1000 ? '100%' : '100%';
  // compact: the small chart of the wide layout
  let chartHeight = containerWidth > 1000 ? 470 : 400;
  if (compact) chartHeight = chartHeightProp || 250;

  if (isLoading) {
    return <CircularProgress />;
  }

  if (failed) {
    return (
      <>
        <Typography component="p" sx={{ mb: 1 }}>
          The Skilldar could not be loaded.
        </Typography>
        <Button variant="outlined" onClick={() => setAttempt((count) => count + 1)}>
          Try again
        </Button>
      </>
    );
  }

  if (noData || !radarData) {
    return (
      <Typography component="p" color="textSecondary">
        No skills data for these careers
      </Typography>
    );
  }

  const categories = radarData.options.xaxis.categories || [];
  const showNearest = (event) => {
    const wrap = wrapRef.current;
    const canvas = wrap?.querySelector('.apexcharts-canvas');
    if (!canvas || !categories.length) return;
    const box = canvas.getBoundingClientRect();
    // the first skill is at the top and the rest follow clockwise
    const angle = (Math.atan2(event.clientX - (box.left + box.width / 2), -(event.clientY - (box.top + box.height / 2))) + 2 * Math.PI) % (2 * Math.PI);
    const index = Math.round(angle / ((2 * Math.PI) / categories.length)) % categories.length;
    const label = String(categories[index] || '').toLowerCase();
    const wrapBox = wrap.getBoundingClientRect();
    setHover({ label: label.charAt(0).toUpperCase() + label.slice(1), x: event.clientX - wrapBox.left, y: event.clientY - wrapBox.top });
  };
  const hoverProps = compact
    ? {
        ref: wrapRef,
        role: 'img',
        'aria-label': `Skilldar chart of: ${categories.map((c) => String(c).toLowerCase()).join(', ')}`,
        onPointerMove: showNearest,
        onPointerDown: showNearest,
        onPointerLeave: () => setHover(null),
        style: { position: 'relative', width: '100%', touchAction: 'pan-y' },
      }
    // full width: inside a centring flex box the chart would otherwise measure its (empty) wrapper as 0 wide and never draw
    : { style: { width: '100%' } };

  return (
    <div {...hoverProps}>
      {compact && hover && (
        <span
          style={{
            position: 'absolute',
            left: hover.x,
            top: Math.max(hover.y - 38, 0),
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
            zIndex: 2,
            background: tone === 'light' ? '#ffffff' : '#12151c',
            color: tone === 'light' ? '#12151c' : '#ffffff',
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1,
            padding: '7px 10px',
            borderRadius: 6,
            whiteSpace: 'nowrap',
          }}
        >
          {hover.label}
        </span>
      )}
      <ApexCharts
        className="skilldarChart"
        style={{
          minHeight: isXs ? '400px !important' : '450px !important',
          width: '100% !important',
          margin: isXs ? '0 auto' : 'unset',
        }}
        type="radar"
        height={isXs && !compact ? 400 : chartHeight}
        width={chartWidth}
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          maxWidth: isXs ? '100%' : 'unset',
        }}
        chartData={radarData}
        options={{
          ...radarData.options,
          xaxis: {
            ...radarData.options.xaxis,
            labels: {
              ...radarData.options.xaxis.labels,
              style: {
                ...radarData.options.xaxis.labels.style,
                fontSize: isXs ? '7px !important' : '10px',
              },
            },
          },
          legend: {
            ...radarData.options.legend,
            fontSize: isXs ? '9px' : '12px',
            itemMargin: {
              horizontal: isXs ? 4 : 8,
              vertical: isXs ? 1 : 4,
            },
          },
        }}
        series={radarData.series}
      />
    </div>
  );
};

WidgetRadarChart.propTypes = {
  containerWidth: PropTypes.number.isRequired,
  careersReskillTime: PropTypes.object,
  onDataLoaded: PropTypes.func,
  showLabel: PropTypes.bool,
  closeLabel: PropTypes.func,
  openLabel: PropTypes.func,
  compact: PropTypes.bool,
  chartHeight: PropTypes.number,
  tone: PropTypes.string,
};

export default WidgetRadarChart;
